import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ui } from '@procuros/datachecks';

import { useEmptyStructure } from 'hooks/useObjectStructure';
import { DataTable, useGenerateWebEDIColumns } from 'components/DataTable';
import { DataTableProvider, useDataTableContext } from 'components/DataTable/contexts/DataTableContext';
import { ColumnTypesInternal, DisplayModes } from 'components/DataTable/Types';
import { Button } from 'components/Form/Button';

import { DocumentSectionError } from '../DocumentSectionError/DocumentSectionError';
import { WebEDISection } from '../WebEDISection/WebEDISection';

export const DocumentPaymentTerms = memo(({ config, mode, isSubmitting }: DocumentPaymentTermsProps) => {
  const { label: title, description, fields } = config;
  if (!fields?.length) {
    return null;
  }
  return (
    <WebEDISection header={{ title, spacing: 'medium', description }}>
      <div className="space-y-4">
        <DocumentSectionError path={DATA_PREFIX} sectionConfig={config} />
        <DataTableProvider name={DATA_PREFIX}>
          <PaymentTermsTable config={fields} mode={mode} isSubmitting={isSubmitting} />
        </DataTableProvider>
      </div>
    </WebEDISection>
  );
});

DocumentPaymentTerms.displayName = 'DocumentPaymentTerms';

const PaymentTermsTable = ({
  mode,
  config,
  isSubmitting,
}: {
  mode: DisplayModes;
  config: ui.PaymentTermsSection['fields'];
  isSubmitting: boolean;
}) => {
  const { t } = useTranslation();
  const { fields, append } = useDataTableContext();
  const updatedConfig = useMemo(() => {
    return config.map((field, index) => {
      if (mode === DisplayModes.view) {
        return {
          ...field,
          extraFields: index === 0 ? { textAlign: 'left' } : { textAlign: 'right' },
        };
      }
      return field;
    });
  }, [config, mode]);

  const columns = useGenerateWebEDIColumns({
    config: updatedConfig,
    ...TABLE_STATIC_CONFIG[mode],
    displayMode: mode,
  });

  const emptyPaymentTerm = useEmptyStructure(fields);
  return (
    <DataTable
      key={mode}
      meta={{ dataPrefix: DATA_PREFIX, mode, isSubmitting }}
      columns={columns}
      data={fields}
      enableGlobalSearch={false}
      showCellBorders={mode === DisplayModes.create || mode === DisplayModes.edit}
      emptyState={
        mode === DisplayModes.create || mode === DisplayModes.edit
          ? t('webedi:payment_terms.empty_state.create.text')
          : t('webedi:payment_terms.empty_state.view.text')
      }
      footerMenu={
        (mode === DisplayModes.create || mode === DisplayModes.edit) && (
          <Button
            variant="secondary"
            size="extra-small"
            LeftIcon={PlusIcon}
            onClick={() => append({ ...emptyPaymentTerm, _internal: { [ColumnTypesInternal.procuros_added]: true } })}
            analyticsId="webedi:payment_terms_add"
            disabled={isSubmitting}
          >
            {t('webedi:payment_terms.add_button.text')}
          </Button>
        )
      }
    />
  );
};

const DATA_PREFIX = 'payment_terms';
const TABLE_STATIC_CONFIG = {
  view: {},
  create: {
    selectableRows: false,
    deletableRows: true,
  },
  edit: {
    selectableRows: false,
    deletableRows: true,
  },
};

type DocumentPaymentTermsProps = {
  config: ui.PaymentTermsSection;
  mode: DisplayModes;
  isSubmitting: boolean;
};
